import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../../services/api';
import { sharingService, ShareableData, ShareableType } from '../../services/sharingService';

interface SupportChatBoxProps {
  currentFormValues?: Record<string, any>;
  currentResults?: any[];
  currentFormName?: string;
  userEmail?: string;
  userName?: string;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  category: string;
  message: string;
}

const COUNTRIES = [
  'Germany', 'United States', 'United Kingdom', 'France', 'Italy', 'Spain', 'Netherlands',
  'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Poland', 'Czech Republic', 'Hungary', 'Romania', 'Bulgaria', 'Greece', 'Portugal',
  'Ireland', 'Canada', 'Australia', 'New Zealand', 'Japan', 'South Korea', 'China',
  'India', 'Brazil', 'Mexico', 'Argentina', 'Chile', 'South Africa', 'Other'
];

const CATEGORIES = [
  'Technical Issue',
  'Feature Request',
  'Calculation Problem',
  'User Interface',
  'Data Import/Export',
  'Performance Issue',
  'Documentation',
  'General Inquiry',
  'Other'
];

export const SupportChatBox: React.FC<SupportChatBoxProps> = ({
  currentFormValues,
  currentResults,
  currentFormName,
  userEmail,
  userName,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [includeContext, setIncludeContext] = useState(true); // Toggle for including context
  
  // Parse userName to get first and last name
  const parseUserName = (name: string | undefined): { firstName: string; lastName: string } => {
    if (!name) return { firstName: '', lastName: '' };
    const parts = name.trim().split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  };

  const { firstName: defaultFirstName, lastName: defaultLastName } = parseUserName(userName);

  const [formData, setFormData] = useState<ContactFormData>({
    firstName: defaultFirstName,
    lastName: defaultLastName,
    email: userEmail || '',
    country: 'Germany', // Default to Germany for Wieland
    category: 'General Inquiry',
    message: '',
  });

  const [, setErrors] = useState<Partial<ContactFormData>>({});

  // Update form data when user info changes
  useEffect(() => {
    const { firstName, lastName } = parseUserName(userName);
    setFormData(prev => ({
      ...prev,
      firstName,
      lastName,
      email: userEmail || prev.email,
    }));
  }, [userName, userEmail]);

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate share URL if shareable data is available
  const generateShareUrl = (): string | undefined => {
    // Use the current route path to determine the base type
    const path = window.location.pathname;
    
    // Simple mapping from route to base type
    const routeToType: Record<string, ShareableType> = {
      '/flooded-evaporator': 'flooded_evaporator_rating', // default
      '/dry-expansion-evaporator': 'dry_expansion_evaporator_rating', // default
      '/condenser': 'condenser_rating', // default
      '/coaxial-evaporator': 'coaxial_evaporator',
      '/coaxial-condenser': 'coaxial_condenser',
      '/cold-plate': 'cold_plate',
      '/special-flooded-evaporator': 'special_flooded_evaporator',
      '/special-condenser': 'special_condenser',
    };

    let detectedType = routeToType[path];
    
    if (!detectedType || (!currentFormValues && !currentResults)) {
      return undefined;
    }

    // Check if we need to refine the type based on input mode
    if (detectedType === 'flooded_evaporator_rating' || 
        detectedType === 'dry_expansion_evaporator_rating' || 
        detectedType === 'condenser_rating') {
      
      // Check if we have results to determine if it's rating or selection
      if (currentResults && currentResults.length > 0) {
        const firstResult = currentResults[0];
        
        // Check for input mode in various possible locations
        const inputMode = firstResult.input_mode || 
                         firstResult.input_data?.input_mode ||
                         firstResult.calculation_mode ||
                         firstResult.input_data?.calculation_mode;
        
        // If it's selection mode, update the type
        if (inputMode === 'selection' || inputMode === 'Selection' || 
            inputMode === 'Flow Rate' || inputMode === 'flow_rate') {
          if (detectedType === 'flooded_evaporator_rating') {
            detectedType = 'flooded_evaporator_selection';
          } else if (detectedType === 'dry_expansion_evaporator_rating') {
            detectedType = 'dry_expansion_evaporator_selection';
          } else if (detectedType === 'condenser_rating') {
            detectedType = 'condenser_selection';
          }
        }
      }
    }

    try {
      // Create shareable data from current form values and results
      const shareData: ShareableData = {
        app: 'thermalx',
        type: detectedType,
        version: '1.0',
        fileName: 'support_request',
        data: {
          input_data: currentFormValues || {},
          results: currentResults || [],
          timestamp: new Date().toISOString(),
          formName: currentFormName,
        } as any
      };
      
      return sharingService.generateShareUrl(shareData);
    } catch (error) {
      console.error('Error generating share URL:', error);
      return undefined;
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setShowError(false);

    try {
      // Generate share URL if available
      const shareUrl = generateShareUrl();
      
      // Prepare the email payload
      const emailPayload = {
        ...formData,
        context: includeContext ? {
          currentForm: currentFormName,
          formValues: currentFormValues,
          results: currentResults,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          shareUrl: shareUrl, // Only include share URL, no page URL
        } : undefined
      };

      // Send email through backend API
      const response = await api.post('/core/ui/support/contact', emailPayload);
      
      if (response.status === 200 || response.status === 201) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          firstName: defaultFirstName,
          lastName: defaultLastName,
          email: userEmail || '',
          country: 'Germany',
          category: 'General Inquiry',
          message: '',
        });
        // Close dialog after success
        setTimeout(() => {
          setIsOpen(false);
        }, 2000);
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending support email:', error);
      setErrorMessage('Failed to send support email. Please try again or contact support.thermalx@wieland.com directly.');
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset form when closing
    setFormData({
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: userEmail || '',
      country: 'Germany',
      category: 'General Inquiry',
      message: '',
    });
    setErrors({});
    setIncludeContext(true); // Reset context toggle to default
  };


  // Get context summary for display
  const getContextSummary = () => {
    const formValuesCount = currentFormValues ? Object.keys(currentFormValues).filter(key => 
      currentFormValues[key] !== undefined && currentFormValues[key] !== null && currentFormValues[key] !== ''
    ).length : 0;
    
    const resultsCount = currentResults ? currentResults.length : 0;
    const hasShareUrl = !!generateShareUrl();
    
    return {
      formName: currentFormName,
      formValuesCount,
      resultsCount,
      hasContext: formValuesCount > 0 || resultsCount > 0,
      hasShareUrl
    };
  };

  const contextSummary = getContextSummary();

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="Support"
        onClick={() => setIsOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Support Contact Form Dialog */}
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh', // Allow up to 90vh but don't force it
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon />
            <Typography variant="h6">Contact ThermalX Support</Typography>
          </Box>
          <Box>
            <IconButton onClick={handleClose} sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Context Information */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', 
            borderBottom: 1, 
            borderColor: 'divider' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                Include Current Context
                {contextSummary?.hasContext && (
                  <Chip 
                    label="Available" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Tooltip title={includeContext ? "Context will be included in your support request" : "Context will not be included in your support request"}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeContext}
                      onChange={(e) => setIncludeContext(e.target.checked)}
                      color="primary"
                      disabled={!contextSummary?.hasContext}
                    />
                  }
                  label=""
                />
              </Tooltip>
            </Box>
            
            {includeContext && contextSummary?.hasContext && (
              <>
                {contextSummary.formName && (
                  <Chip 
                    label={contextSummary.formName} 
                    size="small" 
                    icon={<DescriptionIcon />}
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
                
                {contextSummary.resultsCount > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Results: {contextSummary.resultsCount} items
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon fontSize="small" color="primary" />
                  <Typography variant="caption" color="primary">
                    Current window context will be automatically included in your support request
                  </Typography>
                </Box>
              </>
            )}
            
            {includeContext && !contextSummary?.hasContext && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  No current context available to include
                </Typography>
              </Box>
            )}
            
            {!includeContext && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Context will not be included in your support request
                </Typography>
              </Box>
            )}
            
            {!contextSummary?.hasContext && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="warning.main">
                  No form data or results are currently available to include as context
                </Typography>
              </Box>
            )}
          </Box>

          {/* Contact Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, flex: 1, overflow: 'auto' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Email"
                value={formData.email}
                fullWidth
                required
                size="small"
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    color: 'rgba(0, 0, 0, 0.87)',
                  },
                  '& .MuiInputLabel-root.Mui-disabled': {
                    color: 'rgba(0, 0, 0, 0.6)',
                  },
                }}
              />
              <TextField
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                fullWidth
                required
                size="small"
                select
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                fullWidth
                required
                size="small"
                select
              >
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            
            {/* Message field - spans full width */}
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                fullWidth
                required
                multiline
                rows={6}
                placeholder="Please describe your issue or question in detail..."
              />
            </Box>

            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                startIcon={<SendIcon />}
                sx={{ minWidth: 150 }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success/Error Notifications */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Support message sent successfully! {includeContext ? 'Context was included.' : 'No context was included.'} We'll get back to you soon.
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SupportChatBox;
